(() => {
  const SUBMIT_TIMEOUT_MS = 45000;
  const EMBED_SPEC_MAX_LENGTH = 980;
  const EMBED_FIELD_VALUE_MAX_LENGTH = 200;
  const EMBED_FIELD_LABEL_MAX_LENGTH = 72;
  const EMPLOYMENT_OVERFLOW_VALUE_MAX_LENGTH = 800;
  const SHOPIFY_FORMS_PARAGRAPH_MAX_LENGTH = 1000;
  const EMPLOYMENT_OVERFLOW_CHUNK_LIMIT = 997;
  const EMPLOYMENT_DETAILS_FIELD = 'custom#application_details';
  const EMPLOYMENT_OVERFLOW_FIELDS = [
    'custom#application_details',
    'custom#application_details_2',
    'custom#application_details_3',
  ];

  function walkShadowRoots(root, callback) {
    callback(root);
    root.querySelectorAll?.('*').forEach((el) => {
      if (el.shadowRoot) walkShadowRoots(el.shadowRoot, callback);
    });
    if (root.shadowRoot) walkShadowRoots(root.shadowRoot, callback);
  }

  function findEmbedForm(embedSelector) {
    const host = document.querySelector(embedSelector);
    if (!host) return null;

    let form = null;
    walkShadowRoots(host, (root) => {
      if (form) return;
      const found = root.querySelector?.('[data-testid="form"]');
      if (found) form = found;
    });
    return form;
  }

  function waitForEmbedForm(embedSelector, maxWaitMs = 8000, intervalMs = 100) {
    return new Promise((resolve) => {
      const startedAt = Date.now();

      const tick = () => {
        const form = findEmbedForm(embedSelector);
        if (form) {
          resolve(form);
          return;
        }

        if (Date.now() - startedAt >= maxWaitMs) {
          resolve(null);
          return;
        }

        window.setTimeout(tick, intervalMs);
      };

      tick();
    });
  }

  function setEmbedValue(input, value) {
    if (!input) return;
    const proto =
      input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(input, value ?? '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function fieldLabelFromName(name) {
    return name
      .replace(/^contact\[/, '')
      .replace(/\]$/, '')
      .trim();
  }

  function shorten(text, maxLength) {
    const value = (text || '').trim();
    if (value.length <= maxLength) return value;
    return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
  }

  function fitLinesWithinLimit(lines, maxLength) {
    let summary = '';
    for (const line of lines) {
      const next = summary ? `${summary}\n${line}` : line;
      if (next.length > maxLength) break;
      summary = next;
    }
    return summary;
  }

  function buildFormSummary(form, options = {}) {
    const skipLabels = new Set((options.skipLabels || []).map((s) => s.toLowerCase()));
    const maxLength = options.maxLength ?? EMBED_SPEC_MAX_LENGTH;
    const valueMaxLength = options.valueMaxLength ?? EMBED_FIELD_VALUE_MAX_LENGTH;
    const labelMaxLength = options.labelMaxLength ?? EMBED_FIELD_LABEL_MAX_LENGTH;
    const lines = [];
    const handled = new Set();

    form.querySelectorAll('input[name^="contact["], textarea[name^="contact["], select[name^="contact["]').forEach((field) => {
      if (field.type === 'file' || handled.has(field)) return;
      if (field.type === 'hidden') return;

      if (field.type === 'checkbox') {
        handled.add(field);
        if (field.checked) {
          lines.push(shorten(fieldLabelFromName(field.name), labelMaxLength));
        }
        return;
      }

      if (field.type === 'radio') {
        if (field.checked) {
          const label = shorten(fieldLabelFromName(field.name), labelMaxLength);
          lines.push(`${label}: ${shorten(field.value, 40)}`);
        }
        return;
      }

      handled.add(field);
      const value = (field.value || '').trim();
      if (!value) return;
      const label = fieldLabelFromName(field.name);
      if (skipLabels.has(label.toLowerCase()) || label.toLowerCase() === 'email') return;
      lines.push(`${shorten(label, labelMaxLength)}: ${shorten(value, valueMaxLength)}`);
    });

    const summary = fitLinesWithinLimit(lines, maxLength);
    return summary || options.fallback || '';
  }

  function getEmbedErrors(embedForm) {
    if (!embedForm) return [];

    return [...embedForm.querySelectorAll('[class*="formError"]')]
      .filter((el) => {
        const text = (el.textContent || '').trim();
        if (!text) return false;
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      })
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean);
  }

  function isEmbedSuccess(embedSelector) {
    const host = document.querySelector(embedSelector);
    if (!host) return false;
    let step = null;
    walkShadowRoots(host, (root) => {
      const embed = root.querySelector?.('#app-embed');
      if (embed) step = embed.getAttribute('data-current-step');
    });
    return step === 'success';
  }

  function setSubmitting(button, submitting, idleLabel) {
    if (!button) return;
    button.disabled = submitting;
    button.textContent = submitting ? 'SUBMITTING...' : idleLabel;
    button.classList.toggle('loading', submitting);
  }

  function revealBridgeSuccess(config) {
    const fields = document.querySelector(config.fieldsSelector);
    const success = document.querySelector(config.successSelector);

    if (fields) fields.hidden = true;
    if (success) {
      success.hidden = false;
      success.focus?.();
    }

    if (config.useEmbedSuccess) {
      const embedHost = document.querySelector(config.embedSelector);
      if (embedHost) embedHost.removeAttribute('aria-hidden');
      document.body.classList.add('sg-sw-form-success');
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function findEmbedInput(embedForm, fieldId) {
    if (!embedForm || !fieldId) return null;

    const field = embedForm.querySelector(`[data-testid="field-${fieldId}"]`);
    if (field) {
      if (field.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select')) return field;
      return field.querySelector('input:not([type="checkbox"]):not([type="radio"]), textarea, select');
    }

    return (
      embedForm.querySelector(
        `textarea[name="${fieldId}"], input[name="${fieldId}"], select[name="${fieldId}"]`
      ) || embedForm.querySelector(`textarea[id="${fieldId}"], input[id="${fieldId}"]`)
    );
  }

  function findEmbedFieldRoot(embedForm, fieldId) {
    return embedForm.querySelector(`[data-testid="field-${fieldId}"]`);
  }

  function setEmbedCheckboxGroup(embedForm, fieldId, selectedValues) {
    const field = findEmbedFieldRoot(embedForm, fieldId);
    if (!field || !selectedValues?.length) return;

    field.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      const label = (checkbox.closest('label')?.textContent || checkbox.value || '').trim();
      const shouldCheck = selectedValues.some((value) => {
        const normalized = value.toLowerCase();
        return label.toLowerCase().includes(normalized) || normalized.includes(label.toLowerCase());
      });
      if (shouldCheck && !checkbox.checked) checkbox.click();
    });
  }

  function setEmbedRadio(embedForm, fieldId, value) {
    const field = findEmbedFieldRoot(embedForm, fieldId);
    if (!field || !value) return;

    field.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.value.toLowerCase() === value.toLowerCase() && !radio.checked) radio.click();
    });
  }

  function getEmbedTextLimit(embedForm, fieldId, fallback = EMBED_SPEC_MAX_LENGTH) {
    const input = findEmbedInput(embedForm, fieldId);
    if (!input) return fallback;
    const max = Number(input.maxLength);
    if (Number.isFinite(max) && max > 0) return max;
    return fallback;
  }

  function getEmbedOverflowChunkLimit(embedForm, fieldId) {
    const input = findEmbedInput(embedForm, fieldId);
    if (!input) return EMPLOYMENT_OVERFLOW_CHUNK_LIMIT;

    const domMax = Number(input.maxLength);
    if (Number.isFinite(domMax) && domMax > 0) {
      return Math.min(domMax, SHOPIFY_FORMS_PARAGRAPH_MAX_LENGTH);
    }

    return EMPLOYMENT_OVERFLOW_CHUNK_LIMIT;
  }

  function getOverflowFieldLimits(embedForm, fieldIds) {
    return fieldIds
      .map((fieldId) => {
        if (!findEmbedInput(embedForm, fieldId)) return null;
        return getEmbedOverflowChunkLimit(embedForm, fieldId);
      })
      .filter((limit) => Number.isFinite(limit) && limit > 0);
  }

  function splitTextIntoChunks(text, limits) {
    const chunks = [];
    let remaining = (text || '').trim();
    if (!remaining) return chunks;

    limits.forEach((limit, index) => {
      if (!remaining) return;
      const header =
        index === 0 ? '' : `\n\n--- Application details (part ${index + 1}) ---\n\n`;
      const available = Math.max(0, limit - header.length);
      if (available <= 0) return;

      if (remaining.length <= available) {
        chunks.push(`${header}${remaining}`);
        remaining = '';
        return;
      }

      let sliceEnd = available;
      const lastBreak = remaining.lastIndexOf('\n', sliceEnd);
      if (lastBreak > available * 0.6) sliceEnd = lastBreak;

      chunks.push(`${header}${remaining.slice(0, sliceEnd).trimEnd()}`);
      remaining = remaining.slice(sliceEnd).trimStart();
    });

    return chunks;
  }

  function setEmbedOverflowChunks(embedForm, fieldIds, value) {
    const availableFieldIds = fieldIds.filter((fieldId) => findEmbedInput(embedForm, fieldId));
    if (!availableFieldIds.length || !value) return;

    const limits = getOverflowFieldLimits(embedForm, availableFieldIds);
    const chunks = splitTextIntoChunks(value, limits);

    availableFieldIds.forEach((fieldId, index) => {
      const limit = limits[index] || EMPLOYMENT_OVERFLOW_CHUNK_LIMIT;
      setEmbedValue(
        findEmbedInput(embedForm, fieldId),
        shorten(chunks[index] || '', limit)
      );
    });
  }

  function setEmbedOverflow(embedForm, fieldId, value) {
    const input = findEmbedInput(embedForm, fieldId);
    if (!input || !value) return;
    setEmbedValue(input, shorten(value, getEmbedOverflowChunkLimit(embedForm, fieldId)));
  }

  function getFieldValue(form, id) {
    const field = form?.querySelector(`#${id}`) || document.getElementById(id);
    return (field?.value || '').trim();
  }

  function phoneDigits(value) {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    return digits;
  }

  function setEmbedPhoneNumber(embedForm, value) {
    const digits = phoneDigits(value);
    if (digits.length < 10) return false;

    const input =
      findEmbedInput(embedForm, 'phone_number') ||
      embedForm.querySelector('[data-testid="field-phone_number"]');
    if (!input) return false;

    const formatted = `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

    const applyFormatted = () => {
      input.focus?.();
      input.dispatchEvent(
        new InputEvent('beforeinput', {
          bubbles: true,
          cancelable: true,
          inputType: 'insertFromPaste',
          data: formatted,
        })
      );
      setEmbedValue(input, formatted);
      input.dispatchEvent(
        new InputEvent('input', { bubbles: true, inputType: 'insertFromPaste', data: formatted })
      );
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('blur', { bubbles: true }));
    };

    applyFormatted();

    if (phoneDigits(input.value).length < 10) {
      setEmbedValue(input, '+1 ');
      digits.split('').forEach((char) => {
        input.dispatchEvent(
          new InputEvent('beforeinput', { bubbles: true, inputType: 'insertText', data: char })
        );
        input.dispatchEvent(
          new InputEvent('input', { bubbles: true, inputType: 'insertText', data: char })
        );
      });
      applyFormatted();
    }

    return phoneDigits(input.value).length >= 10;
  }

  function applyOverflow(embedForm, mapping) {
    if (!embedForm || !mapping.overflow) return;

    if (mapping.overflowFields?.length) {
      setEmbedOverflowChunks(embedForm, mapping.overflowFields, mapping.overflow);
    } else {
      setEmbedOverflow(embedForm, mapping.overflowField || EMPLOYMENT_DETAILS_FIELD, mapping.overflow);
    }
  }

  function mapToEmbed(embedForm, mapping) {
    const phoneValue = mapping.phone || mapping.text?.phone_number;

    Object.entries(mapping.text || {}).forEach(([fieldId, value]) => {
      if (fieldId === 'phone_number') return;
      setEmbedValue(findEmbedInput(embedForm, fieldId), value);
    });

    Object.entries(mapping.checkboxes || {}).forEach(([fieldId, values]) => {
      setEmbedCheckboxGroup(embedForm, fieldId, values);
    });

    Object.entries(mapping.radios || {}).forEach(([fieldId, value]) => {
      setEmbedRadio(embedForm, fieldId, value);
    });

    if (phoneValue) {
      setEmbedPhoneNumber(embedForm, phoneValue);
    }

    applyOverflow(embedForm, mapping);

    const consent = embedForm.querySelector('input[type="checkbox"]');
    if (consent && !consent.checked) consent.click();
  }

  function submitEmbed(embedForm) {
    const embedSubmit =
      embedForm.querySelector('[data-testid="btn-form-submit"]') ||
      embedForm.querySelector('button[type="submit"]');
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach((type) => {
      embedSubmit?.dispatchEvent(new MouseEvent(type, { bubbles: true, composed: true, cancelable: true }));
    });
  }

  function initBridge(config) {
    const form = document.querySelector(config.formSelector);
    if (!form || form.dataset.sgBridgeBound === 'true') return;
    form.dataset.sgBridgeBound = 'true';

    const submitBtn = config.submitSelector ? document.querySelector(config.submitSelector) : form.querySelector('[type="submit"]');
    const idleLabel = submitBtn?.dataset.sgIdleLabel || submitBtn?.textContent?.trim() || 'Submit';

    if (submitBtn && !submitBtn.dataset.sgIdleLabel) {
      submitBtn.dataset.sgIdleLabel = idleLabel;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const errorEl = config.errorSelector ? document.querySelector(config.errorSelector) : null;
      if (errorEl) errorEl.hidden = true;

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const mapping = config.mapValues(form);
      const phoneValue = mapping.phone || mapping.text?.phone_number;

      if (phoneValue && phoneDigits(phoneValue).length < 10) {
        if (errorEl) {
          errorEl.textContent = 'Please enter a valid 10-digit phone number and try again.';
          errorEl.hidden = false;
        }
        return;
      }

      setSubmitting(submitBtn, true, idleLabel);

      const liveEmbedForm = await waitForEmbedForm(config.embedSelector);
      if (!liveEmbedForm) {
        setSubmitting(submitBtn, false, idleLabel);
        if (errorEl) {
          errorEl.textContent = 'The form service is still loading. Please wait a moment and try again.';
          errorEl.hidden = false;
        }
        return;
      }

      mapToEmbed(liveEmbedForm, mapping);

      if (phoneValue) {
        let phoneReady = setEmbedPhoneNumber(liveEmbedForm, phoneValue);
        if (!phoneReady) {
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          phoneReady = setEmbedPhoneNumber(findEmbedForm(config.embedSelector) || liveEmbedForm, phoneValue);
        }
        if (!phoneReady) {
          setSubmitting(submitBtn, false, idleLabel);
          if (errorEl) {
            errorEl.textContent = 'Could not prepare your phone number for submission. Please check the Phone field and try again.';
            errorEl.hidden = false;
          }
          return;
        }
      }

      const embedFormForSubmit = findEmbedForm(config.embedSelector) || liveEmbedForm;
      applyOverflow(embedFormForSubmit, mapping);

      await new Promise((resolve) => window.setTimeout(resolve, 80));
      submitEmbed(embedFormForSubmit);

      const startedAt = Date.now();
      const poll = window.setInterval(() => {
        if (isEmbedSuccess(config.embedSelector)) {
          window.clearInterval(poll);
          revealBridgeSuccess(config);
          setSubmitting(submitBtn, false, idleLabel);
          return;
        }

        const errors = getEmbedErrors(findEmbedForm(config.embedSelector));
        if (errors.length) {
          window.clearInterval(poll);
          setSubmitting(submitBtn, false, idleLabel);
          if (errorEl) {
            errorEl.textContent = `Could not submit: ${errors[0]}. Please check your entries and try again.`;
            errorEl.hidden = false;
          }
          return;
        }

        if (Date.now() - startedAt > SUBMIT_TIMEOUT_MS) {
          window.clearInterval(poll);
          setSubmitting(submitBtn, false, idleLabel);
          if (errorEl) {
            errorEl.textContent = config.timeoutMessage || 'Something went wrong. Please try again.';
            errorEl.hidden = false;
          }
        }
      }, 400);
    });
  }

  function getValue(id) {
    return getFieldValue(null, id);
  }

  function splitName(fullName) {
    const trimmed = (fullName || '').trim();
    if (!trimmed) return { first_name: '', last_name: '' };
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { first_name: parts[0], last_name: '' };
    return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
  }

  function getCheckedGroupSuffixes(form, groupPrefix) {
    const values = [];
    form.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
      const label = fieldLabelFromName(checkbox.name);
      if (label.startsWith(groupPrefix)) {
        values.push(label.slice(groupPrefix.length).trim());
      }
    });
    return values;
  }

  function getRadioValue(form, groupName) {
    return form.querySelector(`input[type="radio"][name="contact[${groupName}]"]:checked`)?.value || '';
  }

  const EMPLOYMENT_CORE_SKIP_LABELS = ['name', 'address', 'city', 'state', 'zip code', 'phone', 'email'];

  function initContactBridge() {
    initBridge({
      formSelector: '#ContactForm',
      embedSelector: '.speedgrip-forms-embed.sg-contact-embed',
      fieldsSelector: '.sg-contact-fields',
      successSelector: '#ContactFormSuccess',
      errorSelector: '#sg-contact-error',
      submitSelector: '#ContactForm-submit',
      timeoutMessage: 'Something went wrong sending your message. Please try again or email salesweb@speedgrip.com.',
      useEmbedSuccess: false,
      mapValues(form) {
        return {
          phone: getFieldValue(form, 'ContactForm-phone'),
          text: {
            first_name: getFieldValue(form, 'ContactForm-name'),
            email: getFieldValue(form, 'ContactForm-email'),
            'custom#company': getFieldValue(form, 'ContactForm-company'),
            'custom#message': getFieldValue(form, 'ContactForm-body'),
          },
        };
      },
    });
  }

  function initEmploymentBridge() {
    initBridge({
      formSelector: '#EmploymentApplicationForm',
      embedSelector: '.speedgrip-forms-embed.sg-employment-embed',
      fieldsSelector: '.sg-employment-fields',
      successSelector: '#EmploymentFormSuccess',
      errorSelector: '#sg-employment-error',
      submitSelector: '#EmploymentForm-submit',
      timeoutMessage: 'Something went wrong sending your application. Please try again or email sbaral@speedgrip.com.',
      useEmbedSuccess: false,
      mapValues(form) {
        const nameParts = splitName(getFieldValue(form, 'EaForm-name'));
        const overflow = buildFormSummary(form, {
          skipLabels: EMPLOYMENT_CORE_SKIP_LABELS,
          fallback: 'Employment application (no additional details provided)',
          maxLength:
            EMPLOYMENT_OVERFLOW_FIELDS.length * SHOPIFY_FORMS_PARAGRAPH_MAX_LENGTH,
          valueMaxLength: EMPLOYMENT_OVERFLOW_VALUE_MAX_LENGTH,
        });

        return {
          phone: getFieldValue(form, 'EaForm-phone'),
          text: {
            first_name: nameParts.first_name,
            last_name: nameParts.last_name,
            email: getFieldValue(form, 'EaForm-email'),
            'custom#address': getFieldValue(form, 'EaForm-address'),
            'custom#city': getFieldValue(form, 'EaForm-city'),
            'custom#state': getFieldValue(form, 'EaForm-state'),
            'custom#zipcode': getFieldValue(form, 'EaForm-zip'),
          },
          overflowFields: EMPLOYMENT_OVERFLOW_FIELDS,
          overflow,
        };
      },
    });
  }

  function boot() {
    if (document.getElementById('ContactForm')?.classList.contains('sg-contact-fields')) initContactBridge();
    if (document.getElementById('EmploymentApplicationForm')?.classList.contains('sg-employment-fields')) initEmploymentBridge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
